import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import { useCreateCall } from "@/lib/api/calls.ts";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

interface CallConfirmationModalProps {
  otherUserId: string;
  open: boolean;
  onClose: () => void;
}

export function CallConfirmationModal({
  otherUserId,
  open,
  onClose,
}: CallConfirmationModalProps) {
  const { mutateAsync: createCall, isPending } = useCreateCall();
  const { track } = useTrackEvent();

  const handleCall = async () => {
    try {
      const response = await createCall({
        sdk_id: window.crypto.randomUUID(),
        to_user: otherUserId,
      });

      if (!response) throw new Error("Call not created");

      track(PostHogEvents.CALL_STARTED, {
        teen_id: otherUserId,
        call_id: response.id,
        room_id: response.sendbird_room_id,
      });

      // Open a new window with the call room
      window.open(`/calls/${response.sendbird_room_id}`, "_blank");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
        <div className="p-4">
          <DialogHeader>
            <DialogTitle className="mb-1 text-2xl font-semibold">
              Do you want to call?
            </DialogTitle>
            <DialogDescription className="text-base">
              This will start a call with the other user.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-row items-center gap-4">
            <Button
              className="w-full text-lg font-bold text-slate-900"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              No
            </Button>
            <Button
              className="w-full text-lg font-bold text-white"
              variant="default"
              onClick={async () => {
                await handleCall();
                onClose();
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
